import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';

/**
 * Force-change-password screen. Reached when the logged-in user has
 * `password_must_change: true` (set by an admin "Reset Password"). The
 * router blocks every other route until the user changes their password.
 */
export default function ForceChangePassword() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast.error('Use at least 8 characters');
    if (newPw !== confirm) return toast.error('Passwords do not match');
    if (newPw === current) return toast.error('Pick a password different from the temporary one');
    setSubmitting(true);
    try {
      await apiRequest('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: current, new_password: newPw }),
      });
      toast.success('Password updated');
      if (refreshUser) await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="force-change-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Lock className="text-amber-600" size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Choose your password</h1>
            <p className="text-sm text-slate-500">Hi {user?.name?.split(' ')[0] || 'there'} — your admin reset your password. Pick a new one to continue.</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <label className="text-xs font-medium text-slate-600">Temporary password</label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="current-pw-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">New password</label>
            <Input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              data-testid="new-pw-input"
            />
            <p className="text-xs text-slate-500 mt-1">At least 8 characters.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Confirm new password</label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              data-testid="confirm-pw-input"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="submit-force-change-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Save and continue
          </Button>
        </form>
      </div>
    </div>
  );
}
